// Public auth (Google OAuth) state. Резолвится `/api/public/me`:
// `null` = аноним (401), объект `Me` = залогинен. Любая другая ошибка —
// падает в resource.error. Шарим через context, чтобы Sidebar и Settings
// видели одну и ту же reactive resource (без второго fetch и без race
// между logout и refetch в разных местах).

import { createContext, useContext, createResource } from 'solid-js'
import type { ParentComponent, Resource } from 'solid-js'

// Должен совпадать с MeResponse в ssm_public/src/public_auth.rs.
//
// `linked` = `public_user.linked_user_id IS NOT NULL`. Если `false` — юзер
// залогинился через Google, но админ ещё не связал его с внутренним `User`,
// поэтому ничем владеть не может (все ownership-эндпойнты вернут 403).
// AuthGate использует этот флаг, чтобы показать «awaiting approval»
// вместо рабочего пространства.
export type Me = {
  public_user_id: number
  email: string
  email_verified: boolean
  name: string | null
  picture_url: string | null
  is_active: boolean
  linked: boolean
  permissions: string[]
}

export const LOGIN_URL = '/api/public/auth/google/start'

async function fetchMe(): Promise<Me | null> {
  const resp = await fetch('/api/public/me', { credentials: 'same-origin' })
  if (resp.status === 401) return null
  if (!resp.ok) throw new Error(`GET /api/public/me failed: ${resp.status}`)
  return await resp.json()
}

export async function logoutRequest(): Promise<void> {
  const resp = await fetch('/api/public/auth/logout', {
    method: 'POST',
    credentials: 'same-origin',
  })
  if (!resp.ok) throw new Error(`POST /api/public/auth/logout failed: ${resp.status}`)
}

type AuthCtx = {
  me: Resource<Me | null>
  refetch: () => void
  logout: () => Promise<void>
}

const Ctx = createContext<AuthCtx>()

export const AuthProvider: ParentComponent = (props) => {
  const [me, { refetch }] = createResource(fetchMe)
  const logout = async () => {
    await logoutRequest()
    await refetch()
  }
  return <Ctx.Provider value={{ me, refetch, logout }}>{props.children}</Ctx.Provider>
}

export function useAuth(): AuthCtx {
  const ctx = useContext(Ctx)
  // Контракт: AuthProvider всегда выше по дереву (см. index.tsx).
  return ctx!
}
